import { useEffect, useRef, useState, type ReactNode } from "react";
import { FilterFieldIcon } from "./FilterFieldIcon";
import { useStickyFiltersHeight } from "./useStickyFiltersHeight";

export type CatalogFilterOption = { value: string; label: string };

export type CatalogMultiField = {
  readonly kind: "multi";
  readonly key: string;
  readonly label: string;
  readonly selectId: string;
  readonly options: readonly CatalogFilterOption[];
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
};

export type CatalogSingleField = {
  readonly kind: "single";
  readonly key: string;
  readonly label: string;
  readonly selectId: string;
  readonly options: readonly CatalogFilterOption[];
  readonly value: string;
  readonly onChange: (next: string) => void;
};

export type CatalogSearchField = {
  readonly kind: "search";
  readonly key: string;
  readonly label: string;
  readonly inputId: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly onChange: (next: string) => void;
};

export type CatalogFilterField = CatalogMultiField | CatalogSingleField | CatalogSearchField;

export type NbsCatalogFilterBarProps = {
  readonly ariaLabel: string;
  readonly fields: readonly CatalogFilterField[];
  readonly activeCount: number;
  readonly onReset: () => void;
  readonly trailing?: ReactNode;
};

function toggle(list: readonly string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * Token/chip filter bar shared by catalogue pages (Indicateurs, Mesures).
 * Mirrors the Dashboard / N1 `NbsGlobalFilters` interaction model so users
 * never feel they switched applications when changing screens.
 */
export function NbsCatalogFilterBar({
  ariaLabel,
  fields,
  activeCount,
  onReset,
  trailing,
}: NbsCatalogFilterBarProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  useStickyFiltersHeight(rootRef);

  const multiFields = fields.filter((f): f is CatalogMultiField => f.kind === "multi");
  const singleFields = fields.filter((f): f is CatalogSingleField => f.kind === "single");
  const searchField = fields.find((f): f is CatalogSearchField => f.kind === "search");

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpenKey(null);
        setAddOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenKey(null);
        setAddOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function multiSummary(field: CatalogMultiField): string {
    if (field.value.length === 0) return "";
    if (field.value.length === 1) {
      return field.options.find((o) => o.value === field.value[0])?.label ?? "1 sélectionné";
    }
    return `${field.value.length} sélectionnés`;
  }

  function openPopover(key: string) {
    setQuery("");
    setAddOpen(false);
    setOpenKey((cur) => (cur === key ? null : key));
  }

  function renderMultiPopover(field: CatalogMultiField) {
    const q = query.trim().toLowerCase();
    const visible = q
      ? field.options.filter((o) => o.label.toLowerCase().includes(q))
      : field.options;
    return (
      <div className="nbs-filter-pop" role="dialog" aria-label={field.label}>
        <div className="nbs-filter-pop__searchwrap">
          <input
            className="nbs-filter-pop__search"
            type="text"
            placeholder="Rechercher…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="nbs-filter-pop__opts">
          {visible.map((o) => (
            <label className="nbs-filter-pop__opt" key={o.value}>
              <input
                type="checkbox"
                checked={field.value.includes(o.value)}
                onChange={() => field.onChange(toggle(field.value, o.value))}
              />
              {o.label}
            </label>
          ))}
          {visible.length === 0 ? <p className="nbs-filter-pop__empty">Aucun résultat</p> : null}
        </div>
        <div className="nbs-filter-pop__foot">
          <button type="button" className="nbs-filters__reset" onClick={() => field.onChange([])}>
            Effacer
          </button>
          <button type="button" className="nbs-filter-pop__apply" onClick={() => setOpenKey(null)}>
            Appliquer
          </button>
        </div>
      </div>
    );
  }

  const activeMulti = multiFields.filter((f) => f.value.length > 0);
  const inactiveMulti = multiFields.filter((f) => f.value.length === 0);
  const pendingMulti =
    openKey && !activeMulti.some((f) => f.key === openKey)
      ? (multiFields.find((f) => f.key === openKey) ?? null)
      : null;

  return (
    <div
      className="nbs-global-filters nbs-global-filters--bar nbs-catalog-filter-bar nbs-filters-sticky"
      role="group"
      aria-label={ariaLabel}
      ref={rootRef}
    >
      <div className="nbs-sr-only">
        {multiFields.map((field) => (
          <label key={field.selectId} htmlFor={field.selectId}>
            {field.label}
            <select
              id={field.selectId}
              multiple
              value={field.value as string[]}
              onChange={(e) =>
                field.onChange(Array.from(e.target.selectedOptions).map((o) => o.value))
              }
            >
              {field.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <span className="nbs-filters__lead" aria-hidden="true">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />
        </svg>
        Filtres
      </span>

      {searchField ? (
        <label className="nbs-catalog-filter-bar__search" htmlFor={searchField.inputId}>
          <span className="nbs-sr-only">{searchField.label}</span>
          <input
            id={searchField.inputId}
            type="search"
            value={searchField.value}
            placeholder={searchField.placeholder ?? "Rechercher…"}
            onChange={(e) => searchField.onChange(e.target.value)}
          />
        </label>
      ) : null}

      {activeMulti.map((field) => (
        <div className="nbs-filter-token" key={field.key}>
          <button
            type="button"
            className="nbs-filter-token__body"
            aria-expanded={openKey === field.key}
            onClick={() => openPopover(field.key)}
          >
            <span className="nbs-filter-token__key">{field.label} :</span>
            <span className="nbs-filter-token__val">{multiSummary(field)}</span>
            <svg
              className="nbs-filter-token__caret"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="nbs-filter-token__x"
            aria-label={`Retirer le filtre ${field.label}`}
            onClick={() => field.onChange([])}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
          {openKey === field.key ? renderMultiPopover(field) : null}
        </div>
      ))}

      {pendingMulti ? (
        <div className="nbs-filter-token">
          <button
            type="button"
            className="nbs-filter-token__body"
            aria-expanded="true"
            onClick={() => openPopover(pendingMulti.key)}
          >
            <span className="nbs-filter-token__key">{pendingMulti.label} :</span>
            <span className="nbs-filter-token__val nbs-filter-token__val--empty">tous</span>
          </button>
          {renderMultiPopover(pendingMulti)}
        </div>
      ) : null}

      {inactiveMulti.length > 0 ? (
        <div className="nbs-filter-token nbs-filter-token--add">
          <button
            type="button"
            className="nbs-add-filter"
            aria-expanded={addOpen}
            onClick={() => {
              setOpenKey(null);
              setAddOpen((v) => !v);
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M5 12h14M12 5v14" />
            </svg>
            Ajouter un filtre
          </button>
          {addOpen ? (
            <div className="nbs-filter-pop" role="menu">
              <div className="nbs-filter-pop__title">Filtrer par</div>
              {inactiveMulti.map((field) => (
                <button
                  key={field.key}
                  type="button"
                  className="nbs-filter-pop__row"
                  role="menuitem"
                  onClick={() => {
                    setAddOpen(false);
                    openPopover(field.key);
                  }}
                >
                  <span className="nbs-filter-pop__row-main">
                    <FilterFieldIcon fieldKey={field.key} />
                    {field.label}
                  </span>
                  <span className="nbs-filter-pop__count">{field.options.length}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <span className="nbs-filters__spacer" />

      <div className="nbs-filters__inline">
        {singleFields.map((field) => (
          <label key={field.key} className="nbs-filters__metric" htmlFor={field.selectId}>
            {field.label}
            <select
              id={field.selectId}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
            >
              {field.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      {trailing}

      <button
        type="button"
        className="nbs-filters__reset"
        onClick={onReset}
        disabled={activeCount === 0}
      >
        Réinitialiser
      </button>
    </div>
  );
}
